import { makeStyles } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  cardRoot: {
    borderRadius: 24,
    overflow: "hidden",
    boxShadow: "0 12px 36px rgba(15, 35, 80, 0.1)",
    backgroundColor: "#ffffff",
    width: "100%",
    maxWidth: 860,
    transition: "transform 0.35s ease, box-shadow 0.35s ease",
    "&:hover": {
      boxShadow: "0 20px 48px rgba(15, 35, 80, 0.15)",
    },
  },

  cardActionArea: {
    cursor: "default",
    "&:hover": {
      backgroundColor: "transparent",
    },
    "& .MuiTouchRipple-root": {
      display: "none",
    },
  },

  imageContainer: {
    position: "relative",
    width: "100%",
    height: 420,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      height: 320,
    },
  },

  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "transform 0.5s ease",
    "$cardRoot:hover &": {
      transform: "scale(1.04)",
    },
  },

  activeBadge: {
    position: "absolute",
    bottom: 80,
    left: 24,
    backgroundColor: "#5ad68e",
    color: "#1f6b45",
    padding: "8px 16px",
    borderRadius: 100,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "1.2px",
    userSelect: "none",
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 4px 12px rgba(90, 214, 142, 0.3)",
    transition: "transform 0.3s ease",
    "$cardRoot:hover &": {
      transform: "scale(1.05)",
    },
  },

  activeDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "#2fa264",
    animation: "$pulse 2s infinite",
  },

  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.6,
    },
  },

  imageTitle: {
    position: "absolute",
    bottom: 24,
    left: 24,
    right: 24,
    color: "#ffffff",
    fontWeight: 900,
    fontSize: "2.8rem",
    lineHeight: 1.1,
    textShadow: "0 3px 12px rgba(0,0,0,0.4)",
    zIndex: 3,
    textAlign: "left",
    [theme.breakpoints.down("sm")]: {
      fontSize: "2.2rem",
    },
  },

  checkboxWrapper: {
    position: "absolute",
    top: 20,
    right: 20,
    backgroundColor: "rgba(255,255,255,0.92)",
    borderRadius: 10,
    zIndex: 3,
    transition: "background-color 0.2s ease, transform 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,1)",
      transform: "scale(1.08)",
    },
  },

  checkbox: {
    color: "#041370",
    padding: 8,
  },

  contentSection: {
    padding: theme.spacing(5, 4),
    backgroundColor: "#f8f9fb",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 3),
    },
  },

  infoGrid: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: theme.spacing(3),
    },
  },

  fieldGroup: {
    display: "flex",
    flexDirection: "column",

    "&:last-child": {
      alignItems: "flex-end",
      textAlign: "right",

      [theme.breakpoints.down("xs")]: {
        alignItems: "flex-start",
        textAlign: "left",
      },
    },
  },

  fieldLabel: {
    color: "#8a97b2",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    marginBottom: theme.spacing(1.2),
    textAlign: "left",
    display: "block",
  },

  projectName: {
    color: "#295445",
    fontSize: "2rem",
    fontWeight: 700,
    lineHeight: 1.15,
    cursor: "pointer",
    transition: "color 0.2s ease, transform 0.2s ease",
    "&:hover": {
      color: "#1d3d31",
      transform: "translateX(2px)",
    },
  },

  assetCode: {
    color: "#4857b9",
    fontSize: "1.75rem",
    fontWeight: 800,
    letterSpacing: "0.5px",
    textAlign: "left",
    cursor: "pointer",
    transition: "color 0.2s ease, transform 0.2s ease",
    "&:hover": {
      color: "#5869C5",
      transform: "translateX(2px)",
    },
  },

  metadataBox: {
    backgroundColor: "#ecebf7",
    padding: theme.spacing(3),
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%",
    transition: "background-color 0.3s ease",
    "&:hover": {
      backgroundColor: "#e4e3f4",
    },
  },

  hashRow: {
    backgroundColor: "#f8f8fb",
    borderRadius: 10,
    padding: theme.spacing(1.8, 2.2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#ffffff",
    },
  },

  metadataCode: {
    fontSize: 13,
    color: "#4f5975",
    wordBreak: "break-all",
    lineHeight: 1.4,
    fontFamily: "monospace",
    flex: 1,
  },

  copyButton: {
    border: "none",
    background: "transparent",
    color: "#9aa3bc",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 6,
    borderRadius: 6,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#e8e9f0",
      color: "#5869C5",
      transform: "scale(1.1)",
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  },

  verifiedRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.2),
    backgroundColor: "#edf4f0",
    borderRadius: 10,
    padding: theme.spacing(1.5, 2),
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#e3f0ea",
    },
  },

  verifiedIcon: {
    color: "#2f7f58",
    fontSize: 18,
  },

  verifiedText: {
    color: "#2f7f58",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.3px",
  },

  actionArea: {
    marginTop: theme.spacing(2),
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },

  offsetButton: {
    borderRadius: 100,
    height: 64,
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#2f7f58",
    color: "#ffffff",
    fontSize: 15,
    fontWeight: 800,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    boxShadow: "0 8px 20px rgba(47, 127, 88, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#246b47",
      boxShadow: "0 12px 28px rgba(47, 127, 88, 0.4)",
      transform: "translateY(-3px)",
    },
    "&:active": {
      transform: "translateY(-1px)",
    },
  },
}));

export default useStyles;
